import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentCategory } from '@bonusly/common-action-input';
import { Box, Flex, Text, Tooltip } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { QuickThankYouRecipientIcon } from '../../assets/quick-thank-you-recipient-icon';
import { BonusText } from '../../components/bonus/bonus-text';
import { TokenType } from '../../components/bonus/lib/lexer';
import { ImageList } from '../../components/common/image';
import RelativeTime from '../../components/common/relative-time';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import User from '../../types/user';
import { EmployeeSpotlightAttachment } from '../employee-spotlight';
import { VideoAttachment } from '../video/video-attachment';
import { CommentMenu } from './comment-menu';
import { SpeechBubble } from './speech-bubble';
import { UserChip } from './user-chip';

export interface CommentDisplayProps {
	bonus: Bonus;
	parentBonus: Bonus;
	onEdit: VoidFunction;
	highlightColor?: string;
}

export const appendUserIcon =
	// eslint-disable-next-line react/display-name
	(user: User) => (segment: ReactNode, type: TokenType) => {
		if (type === TokenType.AMOUNT) {
			return (
				<>
					{segment} <UserChip user={user} />
				</>
			);
		}

		return segment;
	};

export const CommentDisplay = ({ bonus, parentBonus, onEdit, highlightColor }: CommentDisplayProps) => {
	const { giver, created_at, include_parent_giver } = bonus;
	const { canUserAddOn, isEditableByUser } = useContextualizedBonusDetails(bonus);

	const { t } = useTranslation('recognition');
	const company = useCurrentCompany();
	const isCommentFromRecipient = useMemo(() => {
		const parentBonusRecipientsIds = parentBonus.receivers.map(({ id }) => id);
		return parentBonusRecipientsIds.includes(bonus.giver.id);
	}, [bonus.giver.id, parentBonus.receivers]);

	const shouldDisplayIcon = isCommentFromRecipient;
	const attachment = (bonus.attachments && bonus.attachments[0]) || null;
	const isVideoBonus = attachment && attachment.category === 'video';
	const disableVideo = company.disable_video_uploads && company.disable_video_recordings;
	const includeVideo = !disableVideo && isVideoBonus;

	return (
		<SpeechBubble ml={2} pt={2} pb={3} px={3} bg={highlightColor} width="100%">
			<Flex alignItems="center" direction="row" gap={2} letterSpacing="-0.01em" lineHeight={6} mb={2}>
				<Text color="text.soft" size="sm" fontWeight="semibold">
					{giver.display_name}
				</Text>
				{shouldDisplayIcon && (
					<Tooltip label={t('bonus_input.attributes.mention_label_short')}>
						<QuickThankYouRecipientIcon />
					</Tooltip>
				)}
				<RelativeTime color="text.softer" date={created_at} fontSize="sm" />
				{isEditableByUser ? <CommentMenu bonus={bonus} onEdit={onEdit} /> : null}
			</Flex>

			{bonus.reason && (
				<BonusText
					bonus={bonus}
					checkForAmount={canUserAddOn}
					decorateSegment={include_parent_giver ? appendUserIcon(parentBonus.giver) : undefined}
					fontSize="lg"
					fontWeight="normal"
					lineHeight="base"
					text={bonus.reason}
				/>
			)}

			{attachment && (
				<Box pt={4}>
					{includeVideo && attachment.category === AttachmentCategory.Video ? (
						<VideoAttachment bonus={bonus} />
					) : attachment.category === AttachmentCategory.EmployeeSpotlight ? (
						<EmployeeSpotlightAttachment attachment={attachment} />
					) : attachment.category === AttachmentCategory.Image || attachment.category === AttachmentCategory.Social ? (
						<ImageList images={bonus.attachments} variant="sm" />
					) : null}
				</Box>
			)}
		</SpeechBubble>
	);
};
